/*
 * <fp-create> (sections/fp-create.liquid): the character video funnel on the shared FpFlow stepper
 * (assets/fp-flow.js). Steps carry data-kind (look | who | from | personal | script | pay); the look step is only
 * rendered for characters with 2+ looks, so step numbers come from the DOM, never from a constant.
 * Fields are keyed by their name attribute (fieldSelector '[name]'), not data-prop.
 * ?package={variant id} and ?look={key} preselect the package and the look. The script step drafts via
 * POST /apps/fp/draft; when window.FP_DRAFT_MOCK is true or that call fails it builds a local template draft
 * from the product's fantastical.sample_line. Pay adds the chosen package to the cart with the line item
 * properties below (the contract with service/src/ledger.ts; names and values unchanged since brief F) and
 * sends the buyer to checkout with their email.
 */
import { FpFlow } from './fp-flow.js?v=20261103b'; // bump when fp-flow.js changes: the bare URL is cached a year at the CDN edge

const MIN_WORDS = 60;
const MAX_WORDS = 110;
const PROPS = [
  'look',
  'recipient',
  'relationship',
  'relationship_other',
  'occasion',
  'occasion_other',
  'from',
  'email',
  'details',
  'avoid',
  'pronunciation',
  'script_mode',
  'script',
  'variant_id',
];
const COUNTED = ['details', 'avoid', 'pronunciation'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CONTACT_RE = /([^\s@]+@[^\s@]+\.[a-z]{2,})|(https?:\/\/|www\.)|(\+?\d[\d\s().-]{7,}\d)/i;

const countWords = (text) => (text.trim().match(/\S+/g) || []).length;
const firstName = (text) => text.trim().split(/\s+/)[0] || '';

class FpCreate extends FpFlow {
  get fieldSelector() {
    return '[name]';
  }

  propOf(el) {
    return el?.name || '';
  }

  get props() {
    return PROPS;
  }

  /** Trimmed, never collapsed: the line item values stay byte-identical to the pre-FpFlow funnel. */
  value(prop) {
    return this.raw(prop).trim();
  }

  setup() {
    this.applyPackageParam();
    this.applyLookParam();
    this.syncConditional();
    this.updateCounts();
    this.updateWords(false);
    this.updatePrice();
    if (this.state.draftNotice) this.setDraftStatus(this.state.draftNotice);
    this.querySelector('[data-fp-draft]').addEventListener('click', () => this.draft());
  }

  applyPackageParam() {
    const param = new URLSearchParams(location.search).get('package');
    const ids = this.config.variants.map((v) => String(v.id));
    if (param && ids.includes(param)) {
      this.setValue('variant_id', param);
    } else if (!ids.includes(this.value('variant_id'))) {
      this.setValue('variant_id', String(this.config.defaultVariantId));
    }
    this.capture();
  }

  /** ?look={key} (from the character page's look pills) preselects a look; otherwise the first look stands. */
  applyLookParam() {
    if (!this.config.looks.length) return;
    const keys = this.config.looks.map((l) => l.key);
    const param = new URLSearchParams(location.search).get('look');
    if (param && keys.includes(param)) {
      this.setValue('look', param);
    } else if (!keys.includes(this.value('look'))) {
      this.setValue('look', keys[0]);
    }
    this.syncLook();
    this.capture();
  }

  syncLook() {
    const key = this.value('look');
    this.querySelectorAll('[data-fp-look-img]').forEach((img) => (img.hidden = img.dataset.fpLookImg !== key));
  }

  /* ---------- derived values ---------- */

  get look() {
    return this.config.looks.find((l) => l.key === this.value('look')) || null;
  }

  get relationship() {
    const r = this.value('relationship');
    return r === '__other' ? this.value('relationship_other') : r;
  }

  get occasion() {
    const o = this.value('occasion');
    return o === '__other' ? this.value('occasion_other') : o;
  }

  get variant() {
    return this.config.variants.find((v) => String(v.id) === this.value('variant_id'));
  }

  /* ---------- hooks ---------- */

  check(prop, v) {
    switch (prop) {
      case 'look':
        return this.config.looks.some((l) => l.key === v) ? '' : `Pick a ${this.config.title}.`;
      case 'recipient':
        return v ? '' : 'Add their first name.';
      case 'relationship_other':
        return this.value('relationship') === '__other' && !v ? 'Describe the relationship, or pick one from the list.' : '';
      case 'occasion_other':
        return this.value('occasion') === '__other' && !v ? 'Tell us the occasion.' : '';
      case 'from':
        return v ? '' : 'Add your first name.';
      case 'email':
        if (EMAIL_RE.test(v)) return '';
        return v ? 'That email address looks incomplete.' : 'Add your email so we can send the video.';
      case 'details':
      case 'avoid':
      case 'pronunciation':
        return v.length <= 400 ? '' : 'Keep this to 400 characters.';
      case 'script': {
        const n = countWords(v);
        if (!n) return 'Draft the script or write your own first.';
        if (n < MIN_WORDS || n > MAX_WORDS) return `The script needs 60 to 110 words; it has ${n}.`;
        return CONTACT_RE.test(v) ? 'Take out any email addresses, links or phone numbers.' : '';
      }
      default:
        return '';
    }
  }

  afterInput(prop) {
    if (prop === 'script') this.updateWords(true);
    if (prop === 'look') this.syncLook();
    if (['script_mode', 'relationship', 'occasion'].includes(prop)) this.syncConditional();
    if (COUNTED.includes(prop)) this.updateCounts();
    if (prop === 'variant_id') this.updatePrice();
  }

  afterShow() {
    document.title = this.config.title;
  }

  /* ---------- conditional fields, counts, price ---------- */

  syncConditional() {
    this.querySelector('[data-fp-relationship-other]').hidden = this.value('relationship') !== '__other';
    this.querySelector('[data-fp-occasion-other]').hidden = this.value('occasion') !== '__other';
    this.querySelector('[data-fp-draft-panel]').hidden = this.value('script_mode') === 'self';
  }

  updateCounts() {
    COUNTED.forEach((name) => {
      const n = this.raw(name).length;
      this.querySelector(`[data-fp-count-for="${name}"]`).textContent = `${n} of 400 characters`;
    });
  }

  updateWords(announce) {
    const n = countWords(this.value('script'));
    let verdict = 'aim for 60 to 110';
    if (n > 0 && n < MIN_WORDS) verdict = `${MIN_WORDS - n} more needed`;
    else if (n > MAX_WORDS) verdict = `${n - MAX_WORDS} over the limit`;
    else if (n >= MIN_WORDS) verdict = 'a good length';
    const text = `${n} ${n === 1 ? 'word' : 'words'} · ${verdict}`;
    const visual = this.querySelector('[data-fp-words-visual]');
    visual.textContent = text;
    visual.classList.toggle('is-ok', n >= MIN_WORDS && n <= MAX_WORDS);
    visual.classList.toggle('is-off', n > MAX_WORDS || (n > 0 && n < MIN_WORDS));

    // Screen readers hear the count once typing pauses, not on every keystroke.
    clearTimeout(this.wordTimer);
    const live = this.querySelector('[data-fp-words-live]');
    if (!announce) {
      live.textContent = `${text}.`;
      return;
    }
    this.wordTimer = setTimeout(() => (live.textContent = `${text}.`), 900);
  }

  updatePrice() {
    const id = this.value('variant_id');
    this.querySelectorAll('[data-fp-price-for]').forEach((p) => (p.hidden = p.dataset.fpPriceFor !== id));
  }

  /* ---------- drafting ---------- */

  setDraftStatus(message) {
    this.querySelector('[data-fp-draft-status]').textContent = message;
  }

  detailsForDraft() {
    const lines = [this.value('details')];
    if (this.value('avoid')) lines.push(`Avoid: ${this.value('avoid')}`);
    if (this.value('pronunciation')) lines.push(`Pronunciation: ${this.value('pronunciation')}`);
    return lines.filter(Boolean).join('\n');
  }

  async draft() {
    const button = this.querySelector('[data-fp-draft]');
    const label = this.querySelector('[data-fp-draft-label]');
    if (button.getAttribute('aria-disabled') === 'true') return;

    if (this.value('script') && !window.confirm('Replace the current script with a new draft?')) return;

    button.setAttribute('aria-disabled', 'true');
    label.textContent = 'Drafting…';
    this.setDraftStatus(`Writing a draft in ${this.config.title}'s voice…`);

    let text = '';
    let draftId = '';
    let notice = '';
    if (window.FP_DRAFT_MOCK === true) {
      notice = 'Test mode: this is a template draft made in your browser, not the script writer.';
    } else {
      try {
        const res = await this.requestDraft();
        text = res.text;
        draftId = res.draft_id;
        notice = 'Here is your draft. Change anything you like, then approve it below.';
      } catch (err) {
        notice =
          err?.status === 429
            ? "You've asked for a lot of drafts this hour, so here is a template draft to edit instead."
            : "The script writer isn't available right now, so here is a template draft to edit instead.";
      }
    }
    if (!text) {
      text = this.templateDraft();
      draftId = `local-${Date.now().toString(36)}`;
    }

    this.setValue('script', text);
    this.state.draftId = draftId;
    this.state.draftNotice = notice;
    this.capture();
    this.updateWords(false);
    this.setError('script', '');
    this.setSummaryError('');

    label.textContent = 'Draft it again';
    button.removeAttribute('aria-disabled');
    this.setDraftStatus(notice);
    this.fields('script')[0].focus();
  }

  async requestDraft() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 25000);
    try {
      const res = await fetch(this.config.draftUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          character: this.config.handle,
          recipient: this.value('recipient'),
          from: this.value('from'),
          relationship: this.relationship,
          occasion: this.occasion,
          details: this.detailsForDraft(),
        }),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.text) throw Object.assign(new Error(data?.error || 'draft failed'), { status: res.status });
      return data;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Offline fallback: the character's sample line supplies the voice, the form supplies the person.
   * The sample line's opening greeting (which names someone else) is dropped and the rest trimmed to fit.
   */
  templateDraft() {
    const name = firstName(this.value('recipient')) || 'friend';
    const from = firstName(this.value('from'));
    const occasion = this.occasion;
    const greetings = {
      Birthday: `Happy birthday, ${name}.`,
      Congratulations: `Congratulations, ${name}.`,
      Wedding: `${name}, on your wedding day, a few words.`,
      Anniversary: `Happy anniversary, ${name}.`,
      'Thank you': `${name}, I come bearing thanks.`,
      'Pep talk': `${name}, a word, if you please.`,
      'Friendly roast': `${name}. Ah, ${name}. Where to begin?`,
      Graduation: `Congratulations on graduating, ${name}.`,
      Retirement: `Happy retirement, ${name}.`,
      'Lost a tooth': `${name}, I hear a tooth came out. How wonderfully brave.`,
    };
    const closings = {
      Birthday: 'May the year ahead be every bit as remarkable as you are.',
      Congratulations: 'You earned this, and I could not be more pleased to say so.',
      Wedding: 'May the two of you keep choosing each other, every single day.',
      Anniversary: 'Here is to every year so far, and to all the ones still coming.',
      'Thank you': 'The world is kinder for having you in it. Thank you.',
      'Pep talk': 'Now go on. You are far more ready than you think.',
      'Friendly roast': 'I jest, of course. Mostly. You are, I admit, rather wonderful.',
      Graduation: 'The next chapter is yours to write. Make it a good one.',
      Retirement: 'You have more than earned every slow morning that lies ahead.',
      'Lost a tooth': 'Tuck it under your pillow tonight, keep brushing the rest, and sleep tight.',
    };

    let sentences = (this.config.sampleLine || '').match(/[^.!?]+[.!?]+["”’)]*/g) || [];
    sentences = sentences.map((s) => s.trim()).filter(Boolean);
    let oldName = '';
    if (sentences.length) {
      oldName = (sentences[0].match(/,\s*([A-Z][a-z]+)\s*[.!?]/) || [])[1] || '';
      sentences.shift();
    }
    sentences = sentences
      .filter((s) => occasion === 'Birthday' || !/birthday/i.test(s))
      .map((s) => (oldName ? s.replace(new RegExp(`\\b${oldName}\\b`, 'g'), name) : s));

    const opener = occasion
      ? greetings[occasion] || `${name}, this one is for your ${occasion.toLowerCase()}.`
      : `${name}, a few words, just for you.`;
    const memory = this.value('details')
      .trim()
      .replace(/[.!?\s]+$/, '')
      .replace(/^(She|He|They|We|You|It)\b/, (w) => w.toLowerCase());
    const memoryLine = memory ? `I am told something you may not wish repeated: ${memory}. Your secret is safe with me.` : '';
    const fromLine = from ? `${from} asked me to pass this along, and I was glad to.` : '';
    const closing = closings[occasion] || 'Enjoy every moment of it.';
    const extra = [
      'Some people simply make a room brighter, and you are one of them.',
      'Do not let anyone tell you otherwise.',
    ];

    const build = (flavour, pad) => [opener, ...flavour, memoryLine, fromLine, ...pad, closing].filter(Boolean).join(' ');
    let flavour = sentences.slice();
    let text = build(flavour, []);
    while (countWords(text) > MAX_WORDS && flavour.length) {
      flavour.pop();
      text = build(flavour, []);
    }
    let pad = [];
    while (countWords(text) < MIN_WORDS && pad.length < extra.length) {
      pad = extra.slice(0, pad.length + 1);
      text = build(flavour, pad);
    }
    return text;
  }

  /* ---------- review + pay ---------- */

  renderSummary() {
    const set = (key, lines) => {
      const dd = this.querySelector(`[data-fp-sum="${key}"]`);
      if (!dd) return;
      dd.replaceChildren(
        ...lines.filter(Boolean).map((line) => {
          const p = document.createElement('p');
          p.textContent = line;
          return p;
        })
      );
      if (!dd.childElementCount) {
        const p = document.createElement('p');
        p.className = 'fp-create__summary-empty';
        p.textContent = 'Nothing added';
        dd.append(p);
      }
    };
    set('look', [this.look ? `${this.config.title}, ${this.look.label}` : '']);
    set('for', [this.value('recipient'), [this.relationship, this.occasion].filter(Boolean).join(' · ')]);
    set('from', [this.value('from'), this.value('email')]);
    set('personal', [
      this.value('details') && `Memory: ${this.value('details')}`,
      this.value('avoid') && `Avoid: ${this.value('avoid')}`,
      this.value('pronunciation') && `Say it like: ${this.value('pronunciation')}`,
    ]);
    set('script', [this.value('script')]);
  }

  /** Line item properties: the contract with service/src/ledger.ts. "For myself" stays "No" since the option went (2026-10-17). */
  properties() {
    const props = {
      Recipient: this.value('recipient'),
      'For myself': 'No',
      Relationship: this.relationship,
      Occasion: this.occasion,
      From: this.value('from'),
      Details: this.value('details'),
      Avoid: this.value('avoid'),
      Pronunciation: this.value('pronunciation'),
      'Approved script': this.value('script'),
      Look: this.look ? this.look.label : '',
      _look: this.look ? this.look.key : '',
      _draft_id: this.state.draftId || 'self-written',
    };
    return Object.fromEntries(Object.entries(props).filter(([, v]) => v !== ''));
  }

  async pay() {
    if (this.busy) return;
    const bad = this.firstInvalidStep();
    if (bad < this.total) {
      this.returnToReview = true;
      this.go(bad);
      return;
    }
    this.setBusy(true, 'Taking you to checkout…');
    this.setSummaryError('');
    const variantId = this.value('variant_id');
    const properties = this.properties();
    try {
      // Coming back from checkout and paying again must not leave two copies of the same brief in the cart.
      await this.removeEarlier(variantId, properties, Object.keys(properties).filter((k) => k !== '_draft_id'));
      await this.addAndCheckout(variantId, properties, this.value('email'));
    } catch (err) {
      this.setBusy(false, 'Pay and create');
      this.setSummaryError(`We couldn't start checkout (${err.message}). Please try again.`);
    }
  }
}

if (!customElements.get('fp-create')) customElements.define('fp-create', FpCreate);
